import React, { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useConnect } from "./connect"
import { Loader } from "~/ui-rtk/components/ui/common"
import ConnectorDialog, { ConnectorDialogProps } from "../ConnectorDialog"

import { defaultImages } from "~/ui-rtk/shared/constants/image-paths.contants"
import { Button } from "~/ui-rtk/components/ui/controls"
import { useCustomConnectorAdditionalConfig } from "~/ui-rtk/hooks/useCustomConnectorAdditionalConfig"
import CustomConfigField from "../forms/CustomConnectorConfigForm/CustomConfigField"

type ConfirmConnectionDialogProps = {
  label: string
  companyConnectorId: string
} & ConnectorDialogProps

const ConfirmConnectionDialog: React.FC<ConfirmConnectionDialogProps> = ({
  label,
  companyConnectorId,
  onClose,
  ...props
}) => {
  const {
    accountInfo,
    confirmConnection,
    deleteConnection,
    isLoading,
    refetchAccountInfo,
  } = useConnect(companyConnectorId)

  const { schema, existingMetadata, completeSetup, isLoadingProperties } =
    useCustomConnectorAdditionalConfig(companyConnectorId)

  // Seed the picker from any value already saved on this connector — covers
  // reopening the dialog mid-flow (e.g. user closed and came back).
  const initialFormData = useMemo<Record<string, string>>(() => {
    const seed: Record<string, string> = {}
    if (!existingMetadata) return seed
    for (const field of schema) {
      const key = field.configuration_path.split(".").pop()
      if (!key) continue
      const saved = (existingMetadata as Record<string, unknown>)[key]
      if (typeof saved === "string") seed[key] = saved
    }
    return seed
  }, [schema, existingMetadata])

  const [formData, setFormData] = useState<Record<string, string>>({})

  useEffect(() => {
    setFormData(prev => ({ ...initialFormData, ...prev }))
  }, [initialFormData])

  const isPickerComplete = useMemo(
    () =>
      schema.every(field => {
        const key = field.configuration_path.split(".").pop()
        if (!key) return false
        return Boolean(formData[key])
      }),
    [schema, formData],
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      if (schema.length > 0) {
        await completeSetup(formData)
        // Account-info endpoint isn't tag-invalidated by the mutation, so
        // pull a fresh copy so any downstream readers see the updated id.
        await refetchAccountInfo()
      }
      await confirmConnection()
      void onClose()
    } catch {
      toast.error(`Failed to confirm connection with ${label}`, {
        toastId: `confirm-connection-failed-${companyConnectorId}`,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    setIsSubmitting(true)
    try {
      await deleteConnection()
      void onClose()
    } catch {
      toast.error(`Failed to delete connection with ${label}`, {
        toastId: `confirm-connection-failed-${companyConnectorId}`,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const dialogClasses = {
    container: "flex flex-col items-center gap-5",
  }

  const avatarAlt = accountInfo
    ? `${accountInfo.accountName} Logo`
    : "Unknown Logo"
  const accountName = accountInfo?.accountName || "Not Provided"
  const accountMetrics = accountInfo?.metric || "Not Provided"

  const isAccountInfo =
    Boolean(accountInfo?.accountName) && Boolean(accountInfo?.metric)

  return (
    <ConnectorDialog onClose={onClose} classes={dialogClasses} {...props}>
      <p className="leading-5 text-base text-platinum text-center">
        Please take a quick moment to verify that the account you connected to{" "}
        {label} is the company's official account.
      </p>
      <div className="flex items-center gap-2">
        <div className="flex gap-4">
          {!isLoading ? (
            <>
              {isAccountInfo ? (
                <>
                  <img
                    src={accountInfo?.avatarUrl || defaultImages.noImage}
                    alt={avatarAlt}
                    width={42}
                    height={42}
                  />
                  <div className="flex flex-col items-start">
                    <b>{accountName}</b>
                    <p>{accountMetrics}</p>
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <div className="w-10.5">
              <Loader className="mb-10" />
            </div>
          )}
        </div>
      </div>
      {!isLoadingProperties && schema.length > 0 && (
        <div className="flex flex-col w-full gap-3 mt-2">
          {schema.map(field => (
            <CustomConfigField
              key={field.configuration_path}
              companyConnectorId={companyConnectorId}
              field={field}
              autoSelectFirst
              value={formData[field.configuration_path.split(".").pop() || ""]}
              onChange={value => {
                const key = field.configuration_path.split(".").pop()
                if (!key) return
                setFormData(prev => ({ ...prev, [key]: value }))
              }}
            />
          ))}
        </div>
      )}
      <div className="flex justify-center w-full gap-5">
        <Button
          variant={{ variant: "solid", color: "white" }}
          isLoading={isSubmitting}
          onClick={handleConfirm}
          disabled={isSubmitting || (schema.length > 0 && !isPickerComplete)}
          className="max-w-50 w-full"
        >
          Confirm
        </Button>
        <Button
          variant={{ variant: "solid", color: "red" }}
          isLoading={isSubmitting}
          onClick={handleDelete}
          className="max-w-50 w-ful"
        >
          Delete Connection
        </Button>
      </div>
    </ConnectorDialog>
  )
}

export default ConfirmConnectionDialog
